import { GenePanelState, LabWorkbookStatus } from '@resistapp/common/types';

type WorkbookReference = {
  id: string;
  status: LabWorkbookStatus;
  genePanelId: number | null | undefined;
  genePanelState?: GenePanelState | null | undefined;
};

const statusColorMap: Partial<Record<LabWorkbookStatus, string>> = {
  [LabWorkbookStatus.DRAFT]: 'gray',
  [LabWorkbookStatus.SAMPLE_SELECTION]: 'cyan',
  [LabWorkbookStatus.SUBMITTED]: 'blue',
  [LabWorkbookStatus.MEASURED]: 'pink',
  [LabWorkbookStatus.PRIMERS_PREPPED]: 'purple',
  [LabWorkbookStatus.SAMPLES_PREPPED]: 'teal',
  [LabWorkbookStatus.RUNNING]: 'orange',
  [LabWorkbookStatus.COMPLETED]: 'green',
  [LabWorkbookStatus.LEGACY]: 'green',
};

const statusStageLabelMap: Partial<Record<LabWorkbookStatus, string>> = {
  [LabWorkbookStatus.DRAFT]: 'Assay template',
  [LabWorkbookStatus.SAMPLE_SELECTION]: 'Sample spec',
  [LabWorkbookStatus.SUBMITTED]: 'DNA QC',
  [LabWorkbookStatus.MEASURED]: 'Primer prep',
  [LabWorkbookStatus.PRIMERS_PREPPED]: 'Sample prep',
  [LabWorkbookStatus.SAMPLES_PREPPED]: 'SmartChip run',
  [LabWorkbookStatus.RUNNING]: 'SmartChip run',
  [LabWorkbookStatus.COMPLETED]: 'Completed',
  [LabWorkbookStatus.LEGACY]: 'Legacy',
};

export function getLabWorkbookStatusColor(status: LabWorkbookStatus): string {
  const color = statusColorMap[status];
  return color ?? 'gray';
}

export function getLabWorkbookStageLabel(status: LabWorkbookStatus): string {
  const label = statusStageLabelMap[status];
  return label ?? status;
}

export function getLabWorkbookSampleSpecHref(
  workbook: Pick<WorkbookReference, 'id' | 'genePanelId' | 'genePanelState'>,
): string {
  return hasValidGeneTemplate(workbook)
    ? `/lab/${workbook.id}/sample-spec`
    : `/lab/${workbook.id}/gene-template?mode=genes`;
}

export function getLabWorkbookStageHref(workbook: WorkbookReference): string {
  // Always link to the workbook root and let the lab router redirect to the best accessible step.
  // This avoids deep-linking customers (and other limited-access users) into steps they cannot access.
  return `/lab/${workbook.id}`;
}

function hasValidGeneTemplate(workbook: Pick<WorkbookReference, 'genePanelId' | 'genePanelState'>): boolean {
  if (workbook.genePanelId == null) {
    return false;
  }
  return workbook.genePanelState === GenePanelState.VALID || workbook.genePanelState === GenePanelState.LOCKED;
}

export function formatWorkbookTimestamp(value: Date | string): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Unknown';
  }
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}
